import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';

/* ------------------------------------------------------------------ */
/*  Frameworks — split into two rows                                    */
/* ------------------------------------------------------------------ */
const row1 = [
  { name: 'GRI',              to: '/frameworks/gri' },
  { name: 'SASB',             to: '/frameworks/sasb' },
  { name: 'TCFD',             to: '/frameworks/tcfd' },
  { name: 'CSRD / ESRS',      to: '/frameworks' },
  { name: 'SEBI BRSR',        to: '/frameworks' },
  { name: 'CDP',              to: '/frameworks/cdp' },
];

const row2 = [
  { name: 'ISSB / IFRS S1 & S2', to: '/frameworks/issb' },
  { name: 'GHG Protocol',        to: '/frameworks' },
  { name: 'UN SDGs',             to: '/frameworks' },
  { name: 'ISO 14001',           to: '/frameworks' },
  { name: 'TNFD',                to: '/frameworks' },
  { name: 'SEC Climate Rule',    to: '/frameworks' },
];

/* ------------------------------------------------------------------ */
/*  Reusable pill                                                       */
/* ------------------------------------------------------------------ */
/* Separator dot between items */
const Dot = () => (
  <span className="text-slate-400 dark:text-slate-500 select-none text-xl font-bold px-1" aria-hidden="true">·</span>
);

const Pill = ({ name, to }) => (
  <>
    <Link
      to={to}
      className="px-6 text-xl font-black text-slate-900 dark:text-white whitespace-nowrap select-none
                 transition-all duration-300
                 hover:text-blue-600 dark:hover:text-blue-400 hover:scale-105"
    >
      {name}
    </Link>
    <Dot />
  </>
);

/* ------------------------------------------------------------------ */
/*  One scrolling row — 4 repeated sets for dense, gapless fill        */
/* ------------------------------------------------------------------ */
const REPEATS = 4;

const MarqueeRow = ({ items, reverse = false }) => {
  const sets = Array.from({ length: REPEATS }, (_, i) => i);
  return (
    <div className="relative w-full overflow-hidden">
      {/* Fade masks */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-gray-50/80 dark:from-slate-900/80 to-transparent z-10" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-gray-50/80 dark:from-slate-900/80 to-transparent z-10" />

      <div className={`flex items-center w-max ${reverse ? 'animate-marquee-reverse' : 'animate-marquee'}`}>
        {sets.map((s) =>
          items.map((fw) => <Pill key={`${s}-${fw.name}`} {...fw} />)
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Component                                                           */
/* ------------------------------------------------------------------ */
const TrustBar = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : { opacity: 0 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      className="bg-gray-50/80 dark:bg-slate-900/80 py-14 md:py-16 overflow-hidden"
    >
      {/* Label */}
      <p className="text-center text-sm uppercase tracking-[0.2em] font-bold text-slate-900 dark:text-white mb-8">
        35+ ESG Frameworks Supported
      </p>

      {/* Row 1 — scrolls left */}
      <MarqueeRow items={row1} />

      {/* Spacer between rows */}
      <div className="mt-5" />

      {/* Row 2 — scrolls right */}
      <MarqueeRow items={row2} reverse />
    </motion.div>
  );
};

export default TrustBar;
